import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import DashboardContext from 'Context';
import { AutoComplete, Col, Row } from 'antd';
import markerSvg from '../../assets/images/marker.png';
import MultiMarker from './MultiMarker';

const throttle = (func, delay) => {
  let lastCall = 0;
  return (...args) => {
    const now = new Date().getTime();
    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    }
  };
};

export default function Mapbox({
  info = {},
  coordinate,
  setAddress,
  setCoordinate,
  width = '100%',
  height = '50vh',
  staticPins = [],
  readonly = false,
  isStatic = false,
  isCombined = false,
  noSearchable = false,
  isContractual = false,
}) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [map, setMap] = useState(null);
  const isParentUpdate = useRef(false);
  const prevCoordinate = useRef(coordinate);
  const [details, setDetails] = useState([]);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const { authentication } = useContext(DashboardContext);
  const [activeFeature, setActiveFeature] = useState(false);
  const [searchBarEnabled, setSearchBarEnabled] = useState(false);

  useEffect(() => {
    // if (mapboxgl.getRTLTextPluginStatus() !== "loaded")
    if (mapboxgl.getRTLTextPluginStatus() === 'unavailable')
      mapboxgl.setRTLTextPlugin(
        'https://cdn.parsimap.ir/third-party/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
        null
      );
  }, []);

  useEffect(() => {
    if (mapContainerRef.current && !map) {
      const mapInstance = new mapboxgl.Map({
        container: mapContainerRef.current,
        style:
          'https://api.parsimap.ir/styles/parsimap-streets-v11?key=p1161ff1fd3ecc4743a88005b41486fb13ced2045b',
        center: [...coordinate].reverse(),
        zoom: isStatic ? 10 : 15,
        accessToken: '',
      });

      if (isStatic) {
        mapRef.current = mapInstance;
      } else {
        mapInstance?.on(
          'moveend',
          throttle(async () => {
            if (!isParentUpdate.current) {
              const center = mapInstance?.getCenter();
              setCoordinate([center.lat, center.lng]);

              const response = await axios.get(
                `https://api.parsimap.ir/geocode/reverse?key=p1a549c4b0187f4ebdade6f9c441a23ac0d78bf40&location=${center?.lng},${center?.lat}`
              );
              setAddress(response?.data?.address);
            }
            isParentUpdate.current = false;
          }, 500)
        );
      }

      if (readonly || !isUnlocked) {
        mapInstance?.dragPan.disable();
        mapInstance?.scrollZoom.disable();
        mapInstance?.boxZoom.disable();
        mapInstance?.doubleClickZoom.disable();
        mapInstance?.touchZoomRotate.disable();
      }

      setMap(mapInstance);
    }
  }, [mapContainerRef, map, coordinate, setCoordinate, readonly]);

  useEffect(() => {
    if (map) {
      if (readonly || !isUnlocked) {
        map.dragPan.disable();
        map.scrollZoom.disable();
        map.boxZoom.disable();
        map.doubleClickZoom.disable();
        map.touchZoomRotate.disable();
      } else {
        map.dragPan.enable();
        map.scrollZoom.enable();
        map.boxZoom.enable();
        map.doubleClickZoom.enable();
        map.touchZoomRotate.enable();
      }
    }
  }, [map, readonly, isUnlocked]);

  useEffect(() => {
    if (
      map &&
      coordinate &&
      (coordinate[0] !== prevCoordinate.current[0] ||
        coordinate[1] !== prevCoordinate.current[1])
    ) {
      isParentUpdate.current = true;
      map.flyTo({
        center: [...coordinate].reverse(),
        essential: true,
        duration: 1000,
      });
      prevCoordinate.current = coordinate;
    }
  }, [coordinate, map]);

  useEffect(() => {
    if (map && isStatic && !isUnlocked) {
      map.dragPan.enable();
      map.scrollZoom.enable();
      map.boxZoom.enable();
      map.doubleClickZoom.enable();
      map.touchZoomRotate.enable();
    }
  }, [map, isStatic, staticPins]);

  useEffect(() => {
    if ((info?.stateId && info?.cityId) || isContractual)
      setSearchBarEnabled(true);
    else setSearchBarEnabled(false);
  }, [info]);

  const handleSearch = async (text) => {
    if (text?.length < 4) return null;

    const response = await axios.get(
      `https://testworkergateway.serviq.net/api/v1/map/searchAutoComplete?searchTerm=${text}&stateId=${
        isContractual
          ? 'dd295da5-96b3-e811-80bc-0cc47aa87eb2'
          : info?.stateId || ''
      }&cityId=${
        isContractual
          ? 'f6295da5-96b3-e811-80bc-0cc47aa87eb2'
          : info?.cityId || ''
      }`,
      {
        headers: {
          Authorization: authentication,
          'Content-Type': 'application/json; charset=utf-8 ',
        },
      }
    );

    setDetails(
      response?.data?.results?.length > 0 ? response?.data?.results : []
    );
  };

  const handleMarker = async (text) => {
    setAddress(text);
    const stats = details?.find((item) => item?.description === text)
      ?.geoLocation?.center;

    if (stats?.lat && stats?.lng) {
      setCoordinate([stats?.lat, stats?.lng]);

      map.flyTo({
        center: [stats?.lng, stats?.lat],
        essential: true,
        duration: 1000,
      });
    }
  };

  const handleMarkerClick = (feature) => {
    setActiveFeature(feature);
  };

  return (
    <Row gutter={24}>
      <Col span={24}>
        {isStatic ? (
          <div style={{ position: 'relative', width: width, height: height }}>
            <div
              ref={mapContainerRef}
              style={{
                height: '100%',
                width: '100%',
                position: 'relative',
                border: '5px solid lightgray',
              }}
            >
              {mapRef.current &&
                staticPins &&
                staticPins?.map((loc) => {
                  return (
                    <MultiMarker
                      isCombined={isCombined}
                      key={loc?.title}
                      map={mapRef.current}
                      feature={loc}
                      isActive={activeFeature?.title === loc?.title}
                      onClick={handleMarkerClick}
                    />
                  );
                })}
            </div>
          </div>
        ) : (
          <div style={{ position: 'relative', width: width, height: height }}>
            <div
              ref={mapContainerRef}
              style={{
                height: '100%',
                width: '100%',
                position: 'relative',
                border: '5px solid lightgray',
              }}
            >
              {!readonly && (
                <div
                  style={{
                    position: 'absolute',
                    top: '2rem',
                    left: '1rem',
                    zIndex: 1000,
                    fontFamily: 'ShabnamFD',
                  }}
                >
                  <button
                    onClick={() => setIsUnlocked((prev) => !prev)}
                    type={'button'}
                    style={{
                      backgroundColor: '#464dee',
                      color: '#ffffff',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      padding: '0.5rem 1rem',
                      cursor: 'pointer',
                    }}
                  >
                    {isUnlocked ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <i
                          className='mdi mdi-lock-open'
                          style={{ fontSize: '18px' }}
                        ></i>
                        باز
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <i
                          className='mdi mdi-lock'
                          style={{ fontSize: '18px' }}
                        ></i>
                        قفل
                      </div>
                    )}
                  </button>
                </div>
              )}
              <div
                style={{
                  width: '100%',
                  position: 'absolute',
                  top: '2rem',
                  padding: '0 10rem',
                  zIndex: '99',
                }}
              >
                {!noSearchable && (
                  <AutoComplete
                    allowClear
                    disabled={!searchBarEnabled || readonly || !isUnlocked}
                    size={'large'}
                    className='map-auto'
                    placeholder={'جستجوی آدرس'}
                    onSearch={handleSearch}
                    onSelect={handleMarker}
                    notFoundContent={'آدرس یافت نشد'}
                    options={details?.map((item) => ({
                      label: item?.description,
                      value: item?.description,
                    }))}
                    getPopupContainer={(trigger) => trigger.parentElement}
                    dropdownStyle={{
                      zIndex: 9999,
                    }}
                    style={{
                      width: '100%',
                      zIndex: 99,
                      position: 'relative',
                    }}
                  />
                )}
              </div>
            </div>
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '40px',
                height: '40px',
                backgroundImage: `url(${markerSvg})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }}
            ></div>
          </div>
        )}
      </Col>
    </Row>
  );
}
